import { logger } from '@sentry/core'
import { fetchClientConfig } from '../services/clientService'
import type { Client, ClientConfigResponse, ClientTheme, UserLogos } from '@app/types'
import { stringStorage } from '../utils/stringStorage'
import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

const PUBLIC_ASSETS_BASE_URL = 'https://d2ufl17mx4sema.cloudfront.net/public/logos'

export const defaultLogos: UserLogos = {
  full: `${PUBLIC_ASSETS_BASE_URL}/openaccess/logo-full.png`,
  simple: `${PUBLIC_ASSETS_BASE_URL}/openaccess/logo-simple.png`,
  whiteFull: `${PUBLIC_ASSETS_BASE_URL}/openaccess/logo-white-full.png`,
  whiteSimple: `${PUBLIC_ASSETS_BASE_URL}/openaccess/logo-white-simple.png`,
}

// save the currently selected client name in localStorage
export const clientNameAtom = atomWithStorage<Client['client_name'] | null>(
  'client_name',
  null,
  stringStorage,
)

// “List of available clients”:
export const availableClients = atom<Client[]>([])

export const selectedClientAtom = atom<Client | null>((get) => {
  const name = get(clientNameAtom)
  const clients = get(availableClients)

  return clients.find((c) => c.client_name === name) ?? null
})

// Async atom: whenever `selectedClientAtom` changes, fetch config
export const clientConfigAtom = atom<Promise<ClientConfigResponse | null>>(async (get) => {
  const selectedClient = get(selectedClientAtom)
  if (!selectedClient) return null
  return await fetchClientConfig()
})

export const clientLocationAtom = atom<Promise<ClientConfigResponse['default_location'] | null>>(
  async (get) => {
    const config = await get(clientConfigAtom)
    return config?.default_location ?? null
  },
)

//  Theme-specific atoms derived from client config
export const clientThemeAtom = atom<Promise<ClientTheme | null>>(async (get) => {
  try {
    const config = await get(clientConfigAtom)

    // Extract theme from client config - the theme is directly in config.theme
    if (config?.theme) {
      return config.theme
    }

    return null
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    logger.warn('Failed to load client theme:', error as any)
    return null
  }
})

export const clientLogoAtom = atom<Promise<UserLogos>>(async (get) => {
  try {
    const config = await get(clientConfigAtom)
    const clientName = get(clientNameAtom)

    if (clientName) {
      return {
        full: `${PUBLIC_ASSETS_BASE_URL}/${clientName}/logo-full.png`,
        simple: `${PUBLIC_ASSETS_BASE_URL}/${clientName}/logo-simple.png`,
        whiteFull: `${PUBLIC_ASSETS_BASE_URL}/${clientName}/logo-white-full.png`,
        whiteSimple: `${PUBLIC_ASSETS_BASE_URL}/${clientName}/logo-white-simple.png`,
      }
    }

    if (config?.logo) {
      return config.logo
    }

    return defaultLogos
  } catch {
    return defaultLogos
  }
})

export const currencySymbolAtom = atom<Promise<string>>(async (get) => {
  try {
    const config = await get(clientConfigAtom)
    if (config?.currency_symbol) {
      return config.currency_symbol
    }
    return '$'
  } catch {
    return '$'
  }
})

export const clientTermsConditionsAtom = atom<Promise<string>>(async (get) => {
  try {
    const config = await get(clientConfigAtom)
    if (config?.tsncs_html) {
      return config.tsncs_html
    }
    return ''
  } catch {
    return ''
  }
})

clientNameAtom.debugLabel = 'clientNameAtom'
availableClients.debugLabel = 'availableClients'
selectedClientAtom.debugLabel = 'selectedClientAtom'
clientConfigAtom.debugLabel = 'clientConfigAtom'
clientThemeAtom.debugLabel = 'clientThemeAtom'
clientLogoAtom.debugLabel = 'clientLogoAtom'
currencySymbolAtom.debugLabel = 'currencySymbolAtom'
clientTermsConditionsAtom.debugLabel = 'clientTermsConditionsAtom'
